import React, { useMemo, useState, useEffect, useRef } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Video from "yet-another-react-lightbox/plugins/video";

import './FullScreen.css'

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/captions.css";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function mapProductToSlides(product) {
  const files = product?.files ?? [];
  return files
    .map((it) => {
      const src = it?.urls?.orig ?? it?.url ?? "";
      if (!src) return null;

      const isVideo = Boolean(it?.metadata?.first_frame_db || it?.isVideo);

      if (isVideo) {
        return {
          type: "video",
          sources: [{ src, type: "video/mp4" }],
          caption: it?.alt ?? "",
          poster: it?.metadata?.first_frame_db?.urls?.orig || "",
          muted: true,
          autoPlay: true,
          loop: true,
          controls: false,
          playsInline: true,
        };
      }
      return { src, alt: it?.alt ?? "", caption: it?.alt ?? "" };
    })
    .filter(Boolean);
}

export default function FullscreenGallery({
  isOpen,
  onClose,
  product,
  initialIndex = 0,
  productKey,            // pass stable product id/slug here
  disableThumbsTest = false,
}) {
  // 1) Build raw slides from product
  const rawSlides = useMemo(() => mapProductToSlides(product), [product]);

  // 2) Freeze slides for the whole open session using productKey.
  //    If productKey changes, we accept a new frozen snapshot.
  const frozenSlidesRef = useRef([]);
  const lastKeyRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return; // avoid pre-freezing while closed
    const changed = productKey && lastKeyRef.current !== productKey;
    if (changed || !frozenSlidesRef.current.length) {
      frozenSlidesRef.current = rawSlides; // freeze snapshot
      lastKeyRef.current = productKey || null;
    }
  }, [isOpen, productKey, rawSlides]);

  // While closed, keep snapshot fresh so we open with current product instantly
  useEffect(() => {
    if (!isOpen) {
      frozenSlidesRef.current = rawSlides;
      lastKeyRef.current = productKey || null;
    }
  }, [isOpen, productKey, rawSlides]);

  const slides = frozenSlidesRef.current;
  const max = Math.max(0, slides.length - 1);
  const desiredIndex = clamp(initialIndex || 0, 0, max);

  // 3) Index is set before mount and not reset during swipes
  const [index, setIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!isOpen || !slides.length) {
      setMounted(false);
      return;
    }
    // Set index BEFORE we mount Lightbox to avoid 0→N jump
    setIndex(desiredIndex);
    setMounted(true);
  }, [isOpen, desiredIndex, slides.length]);

  if (!isOpen || !slides.length || !mounted) return null;

  return (
    <Lightbox
      // no key => no remounts while open
      open={isOpen}
      close={onClose}
      slides={slides}
      index={index}
      zoom={{
        maxZoomPixelRatio: 3,
        zoomInMultiplier: 1.3,
        doubleTapDelay: 250,
        doubleClickDelay: 250,
        keyboardMoveDistance: 50,
      }}
      on={{
        view: ({ index: i }) => {
          // avoid redundant state writes that can trigger extra layout
          if (i !== index) setIndex(i);
        },
      }}
      // A normal swipe animation feels smoother than 0 (which can look like flicker)
      animation={{ swipe: 350, fade: 0 }}
      styles={{
        // container: { backgroundColor: "rgba(0,0,0,0.92)" },
      }}
      carousel={{ finite: false, preload: 3, spacing: 0, padding: 0 }}
      controller={{ closeOnBackdropClick: true }}
      plugins={
        disableThumbsTest ? [Zoom, Captions, Video] : [Zoom, Thumbnails, Captions, Video]
      }
      thumbnails={
        disableThumbsTest
          ? undefined
          : {
            position: "bottom",
            width: 96,
            height: 68,
            gap: 8,
            borderRadius: 8,
            showToggle: true,
          }
      }
      captions={{ descriptionTextAlign: "center" }}
      labels={{
        Close: "Закзрыть",
        Next: "Дальше",
        Previous: "Назад",
        ZoomIn: "Приблизить",
        ZoomOut: "Отдалить",
        ToggleThumbnails: "Отображение миниатюр",
      }}
    />
  );
}
